/**
 * GuarantorAssignedToMePanel — surfaces loans for which the calling
 * employee has been proposed as guarantor (M3X.LOANS §1.15 + §4.1).
 *
 * Fetches via:
 *   GET /api/loans/guarantors/exposure?guarantor_employee_id=<uuid>
 *
 * For each pending row in `by_loan`, the employee can Accept or Decline
 * via the existing endpoints. Consented rows show in a separate section
 * with the option to Revoke (audit-trail preserved).
 *
 * Employee id resolution follows the LoansSelfServicePanel pattern: URL
 * `?employee_id=` query param (HR can impersonate; future PR resolves
 * from Clerk).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { ErrorPanel, Loading } from "./Loading";
import { api } from "../lib/api";

type GuarantorRow = {
  id: string;
  loan_application_id: string;
  guarantor_employee_id: string;
  consent_status: string;
  liability_amount: string;
  currency: string;
  relationship_type: string | null;
  consent_at: string | null;
  decline_reason: string | null;
  revoked_at: string | null;
};

export function GuarantorAssignedToMePanel() {
  const { t } = useTranslation();
  const [search] = useSearchParams();
  const employeeId = search.get("employee_id") ?? "";
  const qc = useQueryClient();
  const [declineFor, setDeclineFor] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState("");

  const exposureQ = useQuery({
    queryKey: ["guarantor", "exposure", employeeId],
    queryFn: () => api.getGuarantorExposure(employeeId),
    enabled: Boolean(employeeId),
  });

  const rowsQ = useQuery({
    queryKey: ["guarantor", "rows-for-employee", employeeId],
    queryFn: () => api.listGuarantorRowsForEmployee(employeeId),
    enabled: Boolean(employeeId),
  });

  const acceptM = useMutation({
    mutationFn: (id: string) => api.acceptGuarantor(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["guarantor"] });
    },
  });
  const declineM = useMutation({
    mutationFn: (id: string) =>
      api.declineGuarantor(id, declineReason || "no reason provided"),
    onSuccess: () => {
      setDeclineFor(null);
      setDeclineReason("");
      qc.invalidateQueries({ queryKey: ["guarantor"] });
    },
  });
  const revokeM = useMutation({
    mutationFn: (id: string) => api.revokeGuarantor(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["guarantor"] });
    },
  });

  if (!employeeId) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
        {t(
          "guarantor_panel.missing_employee_id",
          "Sign in to view your guarantor assignments.",
        )}
      </div>
    );
  }

  if (exposureQ.isLoading || rowsQ.isLoading) return <Loading />;
  if (exposureQ.error)
    return (
      <ErrorPanel
        message={String((exposureQ.error as Error).message ?? exposureQ.error)}
      />
    );

  const rows = (rowsQ.data ?? []) as GuarantorRow[];
  const pending = rows.filter((r) => r.consent_status === "pending");
  const consented = rows.filter((r) => r.consent_status === "consented");
  const historical = rows.filter(
    (r) => r.consent_status === "declined" || r.consent_status === "revoked",
  );

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border-default bg-surface-default p-4">
        <div className="text-xs text-text-tertiary uppercase tracking-wide">
          {t("guarantor_panel.exposure_total", "Total active liability")}
        </div>
        <div className="mt-1 text-2xl font-semibold text-text-primary">
          {exposureQ.data?.total_liability ?? "0"} SAR
        </div>
        <div className="mt-1 text-xs text-text-secondary">
          {exposureQ.data?.active_count ?? 0}{" "}
          {t("guarantor_panel.active_loans", "active loans guaranteed")}
        </div>
      </div>

      <Section title={t("guarantor_panel.pending_section", "Pending consent")}>
        {pending.length === 0 ? (
          <EmptyHint
            text={t(
              "guarantor_panel.no_pending",
              "No pending guarantor requests.",
            )}
          />
        ) : (
          <ul className="space-y-2">
            {pending.map((r) => (
              <li
                key={r.id}
                className="rounded border border-border-default p-3"
              >
                <div className="text-sm">
                  {t("guarantor_panel.liability_for", "Liability:")}{" "}
                  <span className="font-medium">
                    {r.liability_amount} {r.currency}
                  </span>{" "}
                  · {r.relationship_type ?? "—"}
                </div>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    className="rounded bg-emerald-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                    onClick={() => acceptM.mutate(r.id)}
                    disabled={acceptM.isPending}
                  >
                    {t("guarantor_panel.accept", "Accept")}
                  </button>
                  <button
                    type="button"
                    className="rounded border border-slate-300 px-3 py-1.5 text-xs hover:bg-slate-50"
                    onClick={() => setDeclineFor(r.id)}
                  >
                    {t("guarantor_panel.decline", "Decline")}
                  </button>
                </div>
                {declineFor === r.id && (
                  <div className="mt-3 space-y-2">
                    <textarea
                      value={declineReason}
                      onChange={(e) => setDeclineReason(e.target.value)}
                      placeholder={t(
                        "guarantor_panel.decline_reason_placeholder",
                        "Reason for declining",
                      )}
                      className="w-full rounded border border-slate-300 p-2 text-sm"
                      rows={2}
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="rounded bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-50"
                        onClick={() => declineM.mutate(r.id)}
                        disabled={declineM.isPending || !declineReason}
                      >
                        {t(
                          "guarantor_panel.confirm_decline",
                          "Confirm decline",
                        )}
                      </button>
                      <button
                        type="button"
                        className="rounded border border-slate-300 px-3 py-1.5 text-xs"
                        onClick={() => {
                          setDeclineFor(null);
                          setDeclineReason("");
                        }}
                      >
                        {t("guarantor_panel.cancel", "Cancel")}
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title={t("guarantor_panel.consented_section", "Active guarantees")}
      >
        {consented.length === 0 ? (
          <EmptyHint
            text={t(
              "guarantor_panel.no_active",
              "No active guarantor obligations.",
            )}
          />
        ) : (
          <ul className="space-y-2">
            {consented.map((r) => (
              <li
                key={r.id}
                className="rounded border border-border-default p-3"
              >
                <div className="text-sm">
                  {r.liability_amount} {r.currency} · {r.relationship_type ?? "—"}
                </div>
                <div className="mt-2">
                  <button
                    type="button"
                    className="rounded border border-rose-300 px-3 py-1.5 text-xs text-rose-700 hover:bg-rose-50"
                    onClick={() => revokeM.mutate(r.id)}
                    disabled={revokeM.isPending}
                  >
                    {t("guarantor_panel.revoke", "Revoke")}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {historical.length > 0 && (
        <Section
          title={t("guarantor_panel.history_section", "History")}
        >
          <ul className="space-y-1 text-xs text-text-tertiary">
            {historical.map((r) => (
              <li key={r.id}>
                {r.consent_status} · {r.liability_amount} {r.currency}
                {r.decline_reason ? ` — ${r.decline_reason}` : ""}
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3 className="mb-2 text-xs font-medium uppercase text-text-tertiary tracking-wide">
        {title}
      </h3>
      {children}
    </section>
  );
}

function EmptyHint({ text }: { text: string }) {
  return (
    <div className="rounded border border-dashed border-slate-300 p-4 text-center text-sm text-text-secondary">
      {text}
    </div>
  );
}
